// Utility functions for Firebase data serialization
import { Timestamp } from 'firebase/firestore';
import { PropertyEntryModel, ImageWithMetadata, ImageMetadata } from '../types/models';

// Convert Firebase Timestamp to serializable Date string
export const serializeTimestamp = (timestamp: Timestamp | Date | string | undefined): string => {
  if (!timestamp) {
    return new Date().toISOString();
  }
  
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toISOString();
  }
  
  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }
  
  // If it's already a string, return as is (assuming it's ISO string)
  return timestamp;
};

// Convert serialized date string back to Date object
export const deserializeTimestamp = (dateString: string): Date => {
  return new Date(dateString);
};

// Serializable versions for Redux
export interface SerializableImageMetadata extends Omit<ImageMetadata, 'capturedAt'> {
  capturedAt: string; // ISO string instead of Date
}

export interface SerializableImageWithMetadata extends Omit<ImageWithMetadata, 'metadata'> {
  metadata: SerializableImageMetadata;
}

// Serializable version of PropertyEntryModel for Redux
export interface SerializablePropertyEntry extends Omit<PropertyEntryModel, 'createdAt' | 'images'> {
  createdAt: string; // ISO string instead of Date
  images: SerializableImageWithMetadata[];
}

// Convert PropertyEntryModel to serializable format
export const serializePropertyEntry = (entry: PropertyEntryModel): SerializablePropertyEntry => {
  try {
    return {
      ...entry,
      createdAt: entry.createdAt ? entry.createdAt.toISOString() : new Date().toISOString(),
      images: (entry.images || []).map(img => {
        try {
          return {
            ...img,
            metadata: {
              ...img.metadata,
              capturedAt: img.metadata?.capturedAt 
                ? (img.metadata.capturedAt instanceof Date 
                    ? img.metadata.capturedAt.toISOString() 
                    : new Date(img.metadata.capturedAt).toISOString())
                : new Date().toISOString()
            }
          };
        } catch (imgError) {
          console.warn('Error serializing image metadata:', imgError, img);
          return {
            ...img,
            metadata: {
              ...img.metadata,
              capturedAt: new Date().toISOString()
            }
          };
        }
      })
    };
  } catch (error) {
    console.error('Error serializing property entry:', error, entry);
    // Return a fallback serialized entry
    return {
      ...entry,
      createdAt: new Date().toISOString(),
      images: []
    };
  }
};

// Serialize Firebase document data to be Redux-safe
export const serializeFirebaseDocument = (data: any): any => {
  if (!data) return data;
  
  const serialized = { ...data };
  
  // Convert common timestamp fields
  if (serialized.created_at) {
    serialized.created_at = serializeTimestamp(serialized.created_at);
  }
  
  if (serialized.updated_at) {
    serialized.updated_at = serializeTimestamp(serialized.updated_at);
  }
  
  if (serialized.captured_at) {
    serialized.captured_at = serializeTimestamp(serialized.captured_at);
  }
  
  // Handle nested objects
  if (serialized.metadata && typeof serialized.metadata === 'object') {
    serialized.metadata = serializeFirebaseDocument(serialized.metadata);
  }
  
  // Handle arrays of objects
  if (Array.isArray(serialized.images)) {
    serialized.images = serialized.images.map((img: any) => serializeFirebaseDocument(img));
  }
  
  if (Array.isArray(serialized.entries)) {
    serialized.entries = serialized.entries.map((entry: any) => serializeFirebaseDocument(entry));
  }
  
  return serialized;
};

// Serialize report data for Redux (handles sharedAt and other date fields)
export const serializeReportData = (report: any): any => {
  if (!report) return report;
  
  const serialized = { ...report };
  
  // Convert all date fields to ISO strings
  if (serialized.sharedAt) {
    serialized.sharedAt = serializeTimestamp(serialized.sharedAt);
  }
  
  if (serialized.createdAt) {
    serialized.createdAt = serializeTimestamp(serialized.createdAt);
  }
  
  if (serialized.updatedAt) {
    serialized.updatedAt = serializeTimestamp(serialized.updatedAt);
  }
  
  if (serialized.created_at) {
    serialized.created_at = serializeTimestamp(serialized.created_at);
  }
  
  if (serialized.updated_at) {
    serialized.updated_at = serializeTimestamp(serialized.updated_at);
  }
  
  return serialized;
};
